import { applyDriverProfileAppearance, getDriverProfileForKart } from "../driverProfiles";
import { defaultKartWeightForVariant } from "../kartBuild";
import type { KartController, KartState } from "./types";

/** The world-space transform is supplied by the caller so this factory stays
 * independent of any track catalog or starting-grid implementation. */
export interface KartSpawnPose {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

/** Classic/source-indexed state that is meaningful only when the caller has a
 * matching track cursor or classic grid slot. */
export interface KartSpawnExtras {
  readonly sourceSampleIndex?: number;
  readonly classicGrid?: {
    readonly nextCheckpoint: number;
    readonly gatesPassed: number;
    readonly progress: number;
  };
}

export interface CreateKartStateOptions extends KartSpawnExtras {
  readonly id: string;
  readonly index: number;
  readonly controller: KartController;
  readonly distance: number;
  readonly lateral: number;
  readonly pose: KartSpawnPose;
}

/**
 * Construct one authoritative kart with the canonical defaults used by races.
 *
 * Positioning and track-derived cursor values are deliberately inputs. This
 * keeps the shared kart state useful to another simulation (for example a
 * battle session) without making that simulation construct a fake RaceState
 * or teaching this factory about tracks.
 */
export function createKartState(options: CreateKartStateOptions): KartState {
  const { id, index, controller, distance, lateral, pose, sourceSampleIndex, classicGrid } = options;
  const profile = getDriverProfileForKart(id);
  const variant = index % 7;
  const color = profile?.color ?? 0xf3c552;
  const kart: KartState = {
    id,
    name: profile?.name ?? (index === 0 ? "YOU" : `RACER ${index + 1}`),
    color,
    accentColor: 0xf3c552,
    variant,
    livery: 0,
    wheelStyle: index % 5,
    driverColor: color,
    weightClass: defaultKartWeightForVariant(variant),
    pattern: "solid",
    topper: "none",
    sticker: "none",
    underglow: 0,
    horn: "toot",
    skidColor: 0x120c08,
    trail: "bubbles",
    raceNumber: "",
    controller,
    autopilot: false,
    twitchLogin: null,
    twitchDirective: "steady",
    directiveUntil: 0,
    distance,
    lateral,
    ...pose,
    ...(sourceSampleIndex !== undefined ? { classicSampleIndex: sourceSampleIndex } : {}),
    ...(classicGrid ? {
      classicNextCheckpoint: classicGrid.nextCheckpoint,
      classicGatesPassed: classicGrid.gatesPassed,
      classicProgress: classicGrid.progress,
    } : {}),
    velocityX: 0,
    velocityZ: 0,
    speed: 0,
    launchCharge: 0,
    driftCharge: 0,
    driftDirection: 0,
    boostTime: 0,
    itemDodgeTime: 0,
    itemImmunityTime: 0,
    ghostTime: 0,
    draftCharge: 0,
    draftBoostTime: 0,
    draftCooldown: 0,
    draftTargetId: null,
    draftBoostCount: 0,
    heldItem: null,
    shieldTime: 0,
    hiccupTime: 0,
    spinTime: 0,
    spinRecoveryTime: 0,
    itemCooldown: 0,
    itemHeldAt: 0,
    lastUseSeq: 0,
    itemRolls: 0,
    itemHitCount: 0,
    lastItemHit: null,
    itemStealCount: 0,
    lastItemStolen: null,
    itemGuardCount: 0,
    kegDodgeCount: 0,
    lastItemDodge: null,
    pendingItemUse: false,
    laneBias: 0,
    laneBiasUntil: 0,
    hornSeq: 0,
    verticalVelocity: 0,
    airborne: false,
    airTime: 0,
    trickQueued: false,
    classicJumpedSampleIndex: null,
    rampCooldown: 0,
    hazardCooldown: 0,
    courseEventCooldown: 0,
    courseEventHitCount: 0,
    onShortcut: false,
    surface: "road",
    offRoadTime: 0,
    wrongWayTime: 0,
    recoveryTime: 0,
    recoveryCount: 0,
    signatureZoneId: null,
    signatureZoneTime: 0,
    signatureZoneTriggerCount: 0,
    signatureZoneLastLap: 0,
    safeDistance: distance,
    lap: 1,
    place: index + 1,
    finishedAt: null,
  };
  if (profile) applyDriverProfileAppearance(kart, profile);
  return kart;
}
